import { FormInstance, notification } from "@pankod/refine-antd";
import { useGetIdentity, useNavigation, useOne, useUpdate } from "@pankod/refine-core";
import { IBaseProps, IState, IStateTransitionComments, IUser, IWorkListMetadata } from "interfaces";
import React, { useEffect, useState } from "react";
import { DATAPROVIDER_DEFAULT, DATAPROVIDER_LOOKUP, RESOURCE_PATH, STATE_ACTION } from "scripts/site";
import { ButtonDropdown } from './buttonDropdown';
import { RenderCommentsModal } from './commentsModal';

export declare type StateButtonProps = IBaseProps & {
    resourceToAct: string,
    resourceToActId: string,
    /* Optional and Conditional Parameters */
    resourceToNavigate?: string;
    navigateToId?: string;
    hideActions?: string[];
    validateBusinessRules?: () => boolean;
    fetchAssignedToList?: boolean;
    skipAssignedToCheck?: boolean;
    dataProviderName?: string;
    mode?: "standard" | "worklist";
    form?: FormInstance;
    onSuccessFormValidate?: () => void;
    onFailureFormValidate?: () => void;
    setFormPreValidationRules?: (stateAction: string) => void;
    customPostStateAction?: () => void;
};
export const StateButtons: React.FC<StateButtonProps> = (
    {
        resourceToAct,
        resourceToActId,
        resourceToNavigate,
        navigateToId,
        hideActions = [],
        validateBusinessRules,
        fetchAssignedToList = false,
        skipAssignedToCheck = true,
        dataProviderName = DATAPROVIDER_DEFAULT,
        mode = "standard",
        form,
        onSuccessFormValidate,
        onFailureFormValidate,
        setFormPreValidationRules,
        customPostStateAction,
        ...rest
    }) => {

    const { show, list } = useNavigation();
    const { mutate } = useUpdate<IBaseProps>();
    const { data } = useGetIdentity();
    const user = data as IUser;

    const { data: entityMetadata, isLoading } = useOne<IWorkListMetadata>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.METADATA,
        id: resourceToAct.toLowerCase(),
        queryOptions: {
            enabled: true,
            staleTime: 300000
        }
    });

    const [modalVisible, setModalVisible] = useState(false);
    const [actionName, setActionName] = useState<string>("");
    const [currentState, setCurrentState] = useState<IState>();
    const entityStates = entityMetadata?.data.states;

    const handleFormSubmission = (stateAction: string, nextState: string) => {
        var isValid = true;

        // Trigger the business rules validation, if caller is performing the validation
        if (validateBusinessRules) {
            isValid = validateBusinessRules();
            if (isValid) {
                setModalVisible(true);
                setActionName(stateAction);
                return;
            } else {
                notification.error({
                    message: 'Incomplete Details',
                    description:
                        'Please validate all the fields and save before submitting. Click save to preserve your responses.'
                });
                return;
            }
        }

        // Trigger the form Validation, if form to be validated by stateButtons component
        if (form) {
            if (setFormPreValidationRules) setFormPreValidationRules(stateAction);
            // Validate Form and Proceed
            form.validateFields().then((_values) => {
                if (onSuccessFormValidate) onSuccessFormValidate();
                setModalVisible(true);
                setActionName(stateAction);
                return;
            }).catch((_errorInfo) => {
                if (onFailureFormValidate) onFailureFormValidate();
                notification.error({
                    message: 'Incomplete Details',
                    description:
                        'Please validate all the fields and save before submitting. Click save to preserve your responses.'
                });
                return;
            });
        }

        // Default Case - Everything is good, just popup the comments window.
        if (isValid) {
            setModalVisible(true);
            setActionName(stateAction);
            return;
        }

    }

    // Modal operation functions
    const onModalSave = (values: IStateTransitionComments) => {
        setModalVisible(false);
        mutate({
            dataProviderName: dataProviderName,
            resource: `${resourceToAct}/${resourceToActId}/${STATE_ACTION}`,
            id: "",
            values: {
                stateAction: actionName,
                comments: values.comments,
                assignedTo: values.assignedTo,
                computeOnAction: values.computeOnAction
            }
        },
            {
                onSuccess: () => {
                    notification.destroy();
                    notification.success({ key: "notificationKey", message: "Successful", description: `Successfully updated ${resourceToAct}`, type: "success" });

                    if (customPostStateAction) {
                        customPostStateAction(); // In this case Navigation will be responsibility of Component called state Buttons
                    } else if (resourceToNavigate) {
                        navigateToId && navigateToId.length > 0 ?
                            show(resourceToNavigate, navigateToId) :
                            list(resourceToNavigate, "push");
                    } else {
                        list(RESOURCE_PATH.PROFILE);
                    }
                }
            }
        );
    };

    const onModalCancel = () => {
        setModalVisible(false);
        if (onFailureFormValidate) onFailureFormValidate(); // Execute the Failure sequence in Main Window
    };

    const getActions = () => {
        let actions: any[] = [];
        // Add buttons only when all 3 conditions are met
        //  1. If current state is defined
        //  2. Next state exists
        //  3. The state is assigned to current signed-in user
        if (currentState && currentState.nextStates.length > 0 && (skipAssignedToCheck || rest.stateManager.assignedTo.includes(user.id))) {
            currentState.nextStates.forEach((st, indx) => {
                // In case specific state action need to be excluded from button groups
                // This was required for the evaluation form as reassign button is not required.
                if (hideActions && !hideActions.includes(st.action)) {
                    actions.push({
                        key: indx,
                        label: (
                            <div className="st-btn-dv" onClick={() => handleFormSubmission(st.action, st.stateId)}
                                style={{ border: `${st.buttonColor} solid 1px `, paddingLeft: "8px", borderRadius: "2px", color: `${st.buttonColor}` }}>
                                {st.buttonText}
                            </div>
                        )
                    })
                }
            });
        }
        return actions;
    };

    useEffect(() => {
        // Populate Current state when metadata is loaded
        if (entityStates) {
            setCurrentState(entityStates.find(st => st.state === rest.stateManager.state));
        }
    }, [isLoading, entityStates, rest.stateManager.state]);

    if (mode === "worklist") {
        hideActions = entityMetadata?.data?.config?.worklistStatesToHide || [];
    }

    return (
        <>
            {!isLoading && currentState &&
                <>
                    <ButtonDropdown dropdownItems={getActions()} />
                    <RenderCommentsModal
                        visible={modalVisible}
                        modalTitle="Please provide comments:"
                        onCancel={onModalCancel}
                        onSave={onModalSave}
                        stateAction={actionName}
                        entityType={resourceToAct}
                        entityId={resourceToActId}
                        currentState={currentState}
                        fetchAssignedToList={fetchAssignedToList}
                    />
                </>
            }
        </>
    );
};